import React, { useState, useEffect, useRef } from 'react';
import AddTaskButton from './tasks/AddTaskButton';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';
import { StockItem } from '../types';
import '../OrderDetailsDialog.css';
import '../core.css';

interface ProductDetailsDialogProps {
  item: StockItem;
  onClose: () => void;
  refreshStock: () => Promise<void>;
}

const ProductDetailsDialog: React.FC<ProductDetailsDialogProps> = ({ item, onClose, refreshStock }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    gtin: item.gtin || '',
    length: item.length?.toString() || '',
    width: item.width?.toString() || '',
    height: item.height?.toString() || '',
    weight: item.weight?.toString() || '',
    commodity_description: item.commodity_description || '',
    hs_code: item.hs_code || '',
    manufacture_country: item.manufacture_country || '',
    commodity_value: item.commodity_value?.toString() || '',
    fdc_item_number: item.fdc_item_number || '',
    fdc_wms_number: item.fdc_wms_number || '',
    supplier_part_number: item.supplier_part_number || '',
    cost_price: item.cost_price?.toString() || '',
    cost_currency: item.cost_currency || 'USD',
    website_message: item.website_message || '',
    estimated_restock_date: item.estimated_restock_date || '',
  });
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRemoveFromErp = async () => {
    const label = item.type === 'variation' ? `variation "${item.name}"` : `product "${item.name}"`;
    const confirmDelete = window.confirm(
      `Are you sure you want to remove ${label} from the ERP? This action cannot be undone. ` +
      `If this item is still active in WooCommerce, it will be re-imported on the next sync.`
    );
    if (!confirmDelete) return;

    try {
      showToast('Removing from ERP...', 'syncing');
      const productId = item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id;
      const variationId = item.type === 'variation' ? item.id : 0;
      await api.delete(`/delete-product?product_id=${productId}&variation_id=${variationId}`);
      showToast('Removed from ERP successfully', 'success');
      await refreshStock();
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to remove from ERP';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      console.error('Remove from ERP error:', err);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleSave = async () => {

    try {
      showToast('Saving product details...', 'syncing');

      // Check if website message or estimated restock date changed
      const websiteMessageChanged = formData.website_message !== (item.website_message || '');
      const estimatedRestockDateChanged = formData.estimated_restock_date !== (item.estimated_restock_date || '');

      // Prepare updates for the API
      const updates = {
        product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
        variation_id: item.type === 'variation' ? item.id : 0,
        gtin: formData.gtin || null,
        length: formData.length ? parseInt(formData.length) : null,
        width: formData.width ? parseInt(formData.width) : null,
        height: formData.height ? parseInt(formData.height) : null,
        weight: formData.weight ? parseInt(formData.weight) : null,
        commodity_description: formData.commodity_description || null,
        hs_code: formData.hs_code || null,
        manufacture_country: formData.manufacture_country || null,
        commodity_value: formData.commodity_value ? parseInt(formData.commodity_value) : null,
        fdc_item_number: formData.fdc_item_number || null,
        fdc_wms_number: formData.fdc_wms_number || null,
        supplier_part_number: formData.supplier_part_number || null,
        cost_price: formData.cost_price ? parseFloat(formData.cost_price) : null,
        cost_currency: formData.cost_currency || 'USD',
      };

      // Send product details updates if any fields changed
      const hasProductDetailsChanges = Object.keys(updates).some(key =>
        key !== 'product_id' && key !== 'variation_id' &&
        updates[key as keyof typeof updates] !== (item[key as keyof StockItem] || null)
      );

      if (hasProductDetailsChanges) {
        await api.post('/update-product-details', {
          product_details_updates: [updates],
        });
      }

      // Send website message/restock date updates if changed
      if (websiteMessageChanged || estimatedRestockDateChanged) {
        await api.post('/update-estimated-restock-date', {
          product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
          variation_id: item.type === 'variation' ? item.id : 0,
          estimated_restock_date: formData.estimated_restock_date || null,
          website_message: formData.website_message || null,
        });
      }

      showToast('Product details updated successfully', 'success');
      await refreshStock();
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to save product details';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      console.error('Save product details error:', err);
      setTimeout(() => setError(null), 5000);
    }
  };

  return (
    <div className="order-dialog-overlay">
      <div className="order-dialog" ref={dialogRef}>
        <button className="close-button" onClick={onClose}>✕</button>
        {item.sku && (
          <AddTaskButton className="tk-add-task-dialog" link={{ link_type: 'product', link_ref: item.sku, link_label: item.name }} />
        )}
        <div className="order-dialog-content">
          <div className="address-section">
            <h3 className="order-title">Product Details</h3>
            <h4>Product Information</h4>
            <p><strong>Name:</strong> {item.name}</p>
            <p><strong>SKU:</strong> {item.sku || 'N/A'}</p>
            <p><strong>Type:</strong> {item.type}</p>
            <p><strong>Last Updated:</strong> {item.last_updated ? new Date(item.last_updated).toLocaleString() : 'N/A'}</p>
          </div>
          <div className="actions-section">
            <h4>Editable Fields</h4>

            {/* Container 1: Dimensions and Customs */}
            <div className="two-column-container">
              <div className="left-column">
                <div className="details-row">
                  <div className="detail-column">
                    <label>GTIN</label>
                    <input
                      type="text"
                      value={formData.gtin}
                      onChange={(e) => handleInputChange('gtin', e.target.value)}
                      className="detail-column input"
                      maxLength={14}
                    />
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>Length</label>
                    <input
                      type="number"
                      value={formData.length}
                      onChange={(e) => handleInputChange('length', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>Width</label>
                    <input
                      type="number"
                      value={formData.width}
                      onChange={(e) => handleInputChange('width', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>Height</label>
                    <input
                      type="number"
                      value={formData.height}
                      onChange={(e) => handleInputChange('height', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>Weight</label>
                    <input
                      type="number"
                      value={formData.weight}
                      onChange={(e) => handleInputChange('weight', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
              </div>
              <div className="right-column-normal">
                <div className="details-row">
                  <div className="detail-column">
                    <label>Customs Description</label>
                    <input
                      type="text"
                      value={formData.commodity_description}
                      onChange={(e) => handleInputChange('commodity_description', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>HS Code</label>
                    <input
                      type="text"
                      value={formData.hs_code}
                      onChange={(e) => handleInputChange('hs_code', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>Manufacture Country</label>
                    <input
                      type="text"
                      value={formData.manufacture_country}
                      onChange={(e) => handleInputChange('manufacture_country', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>Customs Value</label>
                    <input
                      type="number"
                      value={formData.commodity_value}
                      onChange={(e) => handleInputChange('commodity_value', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Container 2: Supplier and FDC */}
            <div className="two-column-container" style={{ marginTop: '20px' }}>
              <div className="left-column">
                <div className="details-row">
                  <div className="detail-column">
                    <label>Supplier Part Number</label>
                    <input
                      type="text"
                      value={formData.supplier_part_number}
                      onChange={(e) => handleInputChange('supplier_part_number', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>Cost Price</label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.cost_price}
                        onChange={(e) => handleInputChange('cost_price', e.target.value)}
                        className="detail-column input"
                        style={{ flex: 1 }}
                      />
                      <select
                        value={formData.cost_currency}
                        onChange={(e) => handleInputChange('cost_currency', e.target.value)}
                        className="detail-column input"
                        style={{ width: '80px', cursor: 'pointer' }}
                        title="Cost price currency"
                      >
                        <option value="USD">USD</option>
                        <option value="CAD">CAD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="right-column-normal">
                <div className="details-row">
                  <div className="detail-column">
                    <label>FDC Item Number</label>
                    <input
                      type="text"
                      value={formData.fdc_item_number}
                      onChange={(e) => handleInputChange('fdc_item_number', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>FDC WMS Item Number</label>
                    <input
                      type="text"
                      value={formData.fdc_wms_number}
                      onChange={(e) => handleInputChange('fdc_wms_number', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Container 3: Website and Restock */}
            <div className="two-column-container" style={{ marginTop: '20px' }}>
              <div className="left-column">
                <div className="details-row">
                  <div className="detail-column">
                    <label>Website Message</label>
                    <input
                      type="text"
                      value={formData.website_message}
                      onChange={(e) => handleInputChange('website_message', e.target.value)}
                      className="detail-column input"
                      placeholder="Current website message"
                    />
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-column">
                    <label>Estimated Restock Date</label>
                    <input
                      type="date"
                      value={formData.estimated_restock_date}
                      onChange={(e) => handleInputChange('estimated_restock_date', e.target.value)}
                      className="detail-column input"
                    />
                  </div>
                </div>
              </div>
              <div className="right-column-normal">
                {/* Empty right column */}
              </div>
            </div>
          </div>
        </div>
        {error && <div className="error-message">{error}</div>}
        <div className="order-dialog-actions">
          <button className="action-button delete-button" onClick={handleRemoveFromErp}>
            Remove from ERP
          </button>
          <button className="action-button save-button" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsDialog;